"use client";

import { ArrowDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SubmittalServices() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const featuredSpecs = [{
    latin: "Technical Documentation",
    english: "Compliance Review"
  }, {
    latin: "Material Certification",
    english: "Quality Assurance"
  }, {
    latin: "Project Specifications",
    english: "Regulatory Approval"
  }];
  return <section data-section-id="2033" id="service-hero-submittals" className="bg-background text-foreground relative overflow-hidden py-32 md:py-44">
      {}
      <div className="absolute top-0 left-0 right-0 flex items-center" aria-hidden="true">
        <div className="h-px flex-1 bg-border/60" />
        <div className="h-1.5 w-1.5 rounded-lg bg-primary mx-[8.33%]" />
        <div className="h-px flex-1 bg-border/60" />
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div ref={leftRef} className={`lg:col-span-7 order-2 lg:order-1 transition-all duration-[720ms] ease-[cubic-bezier(0.2,0.7,0.2,1)] ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <div className="flex items-center gap-3 mb-10 md:mb-14">
              <span className="font-mono text-[11px] md:text-[13px] tracking-[0.18em] uppercase text-muted-foreground">
                § 01 — Submittal Management
              </span>
              <div className="h-px flex-1 max-w-[120px] bg-border/50" />
              <span className="h-1.5 w-1.5 rounded-lg bg-primary" aria-hidden="true" />
            </div>

            {}
            <h1 className="font-default font-bold tracking-[-0.03em] leading-[0.95] text-[clamp(2.75rem,6.5vw,6.5rem)] text-foreground mb-10">
              <span className="block">Precision in</span>
              <span className="block">every{" "}
                <span className="text-primary font-bold">submittal</span>
              </span>
              <span className="block">for your project</span>
            </h1>

            {}
            <p className="font-default text-[clamp(1.0625rem,1.15vw,1.1875rem)] leading-[1.6] text-muted-foreground max-w-[44ch] mb-12">
              Streamlined documentation workflows designed for complex construction environments. We manage the technical review process from initial specification to final approval, ensuring total compliance and project continuity.
            </p>

            {}
            <div className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8 mb-16 lg:mb-20">
              <Button asChild className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-7 py-6 h-auto text-[13px] tracking-[0.08em] uppercase font-bold transition-all duration-240 hover:translate-x-[2px] shadow-md">
                <Link to="/ServicesPage">View Services</Link>
              </Button>

              <Link to="/ContactPage" className="inline-flex items-center gap-2 font-default text-[14px] text-foreground underline underline-offset-[6px] decoration-1 decoration-muted-foreground hover:decoration-primary hover:text-primary hover:underline-offset-4 transition-all duration-200">
                Request Consultation
                <ArrowDown className="h-3.5 w-3.5" strokeWidth={1.25} />
              </Link>
            </div>

            {}
            <div className="border-t border-border/50 pt-8 max-w-[44ch] hidden lg:block">
              <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-muted-foreground mb-4">
                Core Competencies — This Phase
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                {featuredSpecs.map((spec, i) => <div data-index={i} key={spec.latin} className="flex items-center gap-3">
                    <span className="font-default font-semibold text-[clamp(0.8125rem,0.9vw,0.9375rem)] text-foreground/90">
                      {spec.latin}
                    </span>
                    <span className="font-default text-[12px] text-muted-foreground">
                      ({spec.english})
                    </span>
                    {i < featuredSpecs.length - 1 && <span className="h-1.5 w-1.5 rounded-lg bg-primary ml-1" aria-hidden="true" />}
                  </div>)}
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-5 order-1 lg:order-2 relative transition-all duration-[720ms] ease-[cubic-bezier(0.2,0.7,0.2,1)] delay-[120ms] ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <div className="relative">
              {}
              <span className="absolute -top-2 -left-2 h-2 w-2 border-l border-t border-muted-foreground" aria-hidden="true" />
              <span className="absolute -top-2 -right-2 h-2 w-2 border-r border-t border-muted-foreground" aria-hidden="true" />
              <span className="absolute -bottom-2 -left-2 h-2 w-2 border-l border-b border-muted-foreground" aria-hidden="true" />
              <span className="absolute -bottom-2 -right-2 h-2 w-2 border-r border-b border-muted-foreground" aria-hidden="true" />

              <div className="relative overflow-hidden aspect-[4/5] bg-card rounded-lg shadow-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/submittal_technical_review.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/submittal_technical_review.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/submittal_technical_review.png-wvc-sizes" alt="Professional reviewing technical construction documents and material submittals in a modern office" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>

              {}
              <div className="mt-4 flex items-start justify-between gap-4">
                <p className="font-default font-medium text-[clamp(0.8125rem,0.9vw,0.9375rem)] text-muted-foreground leading-[1.4]">
                  Phase I — <span className="text-foreground/80">Documentation & Compliance Audit</span>
                </p>
                <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-muted-foreground whitespace-nowrap">
                  HQ · Operations
                </span>
              </div>
            </div>

            {}
            <div className="border-t border-border/50 pt-6 mt-10 lg:hidden">
              <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-muted-foreground mb-3">
                Core Competencies
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                {featuredSpecs.map((spec, i) => <div data-index={i} key={spec.latin} className="flex items-center gap-2">
                    <span className="font-default font-semibold text-[14px] text-foreground/90">
                      {spec.latin}
                    </span>
                    <span className="font-default text-[12px] text-muted-foreground">
                      ({spec.english})
                    </span>
                    {i < featuredSpecs.length - 1 && <span className="h-1.5 w-1.5 rounded-lg bg-primary ml-1" aria-hidden="true" />}
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="hidden xl:flex absolute right-6 top-1/2 -translate-y-1/2 items-center" aria-hidden="true">
        <div className="flex flex-col items-center gap-3">
          <span className="h-px w-6 bg-muted-foreground/50" />
          <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-muted-foreground/70" style={{
          writingMode: "vertical-rl"
        }}>
            Project Cycle · Q1 MMXXV
          </span>
          <span className="h-1.5 w-1.5 rounded-lg bg-primary" />
        </div>
      </div>
    </section>;
}